// IMPORTANT: Import global less first to avoid CSS cascading issues
import '../less/_app.global.less';

import 'src/lib/polyfills';
import 'src/lib-server/django-shims';
import {Hydrate, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {useStaticRendering} from 'mobx-react-lite';
import NextApp, {AppContext} from 'next/app';
import {useRouter} from 'next/router';
import {ReactNode, useEffect, useMemo, useState} from 'react';

import {CommonStoresProvider} from '@udemy/common-stores';
import {detectKeyboardNavigation} from '@udemy/design-system-utils';
import {I18nProvider, resolveLocaleDirection, useI18n, UdemyLocale} from '@udemy/i18n';
import {
    APP_CONTEXT_VAR_NAME,
    getClientAppContext,
    mergeWithArrayOverwrite,
    RENDER_MODES,
} from '@udemy/nextjs-core';
import {shimUDPerformance} from '@udemy/performance-rum-client';
import {FbtBundleConfigContext} from '@udemy/react-discovery-units';
import {ExperimentationProvider} from '@udemy/react-experimentation-provider';
import {ShoppingClient} from '@udemy/shopping';
import {StoreProvider} from '@udemy/store-provider';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';
import {UDData, UDDataConfig, UDDataProvider, useUDData, useUDLink} from '@udemy/ud-data';

import {i18nPathToBundleMap, i18nPreferred} from 'next.config.i18n';
import {BindI18nGlobals} from 'src/components/bind-i18n-globals/bind-i18n-globals';
import {AppWithLayoutProps} from 'src/components/layout/types';
import {useDevice} from 'src/hooks/useDevice';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {
    CAREER_ACADEMIES,
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    COURSE_GUIDANCE_FEATURE,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    DESKTOP_POST_ADD_TO_CART_BEHAVIOR,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    LEARNERS_ARE_VIEWING_ON_LOHP,
    LOHP_ABOVE_FOLD_MODULE_REORDER,
    LOHP_CAROUSEL_HOVER_POPUP_REMOVAL,
    POPULAR_CATEGORIES_MODULE,
    POPULAR_CATEGORIES_MODULE_FOR_LOHP,
    SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER,
    MOBILE_WEB_OPTIMIZATION,
    LOHP_VERTICAL_MOBILE_RECO_UNITS,
    FBT_FORCE_GO_TO_CART,
    SHOW_VERNACULAR_CONTENT,
    MWEB_SEARCH_HEADER_OPTIMIZATION,
    PERSONAL_PLAN_BANNER_VALUE_PROP_REVAMP,
    FBT_BUNDLE_SERVICE_VOUCHERS,
} from 'src/lib/experimentation-helper';
import {configureUdApi} from 'src/lib/ud-global-fetch';
import {IntegrationsStore} from 'src/stores/integrations-store';
import {RootStore} from 'src/stores/root-store';

// Configure udApi
configureUdApi();

// TODO: rehyrdate from pageProps instead
const queryClient = new QueryClient();

export const EXPERIMENTATION_FEATURES = [
    CAREER_ACADEMIES,
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    COURSE_GUIDANCE_FEATURE,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    DESKTOP_POST_ADD_TO_CART_BEHAVIOR,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    LEARNERS_ARE_VIEWING_ON_LOHP,
    POPULAR_CATEGORIES_MODULE,
    POPULAR_CATEGORIES_MODULE_FOR_LOHP,
    SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER,
    MOBILE_WEB_OPTIMIZATION,
    LOHP_VERTICAL_MOBILE_RECO_UNITS,
    FBT_FORCE_GO_TO_CART,
    SHOW_VERNACULAR_CONTENT,
    MWEB_SEARCH_HEADER_OPTIMIZATION,
    PERSONAL_PLAN_BANNER_VALUE_PROP_REVAMP,
    LOHP_ABOVE_FOLD_MODULE_REORDER,
    FBT_BUNDLE_SERVICE_VOUCHERS,
    LOHP_CAROUSEL_HOVER_POPUP_REMOVAL,
];

/**
 * App-wide invariant: every route needs Consumersubscription plan data (e.g. Header/SmartBar).
 * Routes that need additional product types layer their own SubscriptionPlansProvider underneath
 * this one, since nested providers now merge context instead of replacing it.
 */
export const SUBSCRIPTION_PLAN_PRODUCT_TYPES = [SubscriptionPlanProductType.Consumersubscription];

/**
 * Prevent observable components to re-render on the server. Other wise this causes a memory leak since lots of mobx
 * references being created on the server and never disposed.
 * @see https://github.com/mobxjs/mobx/tree/main/packages/mobx-react-lite#enablestaticrenderingenable-true
 */
// eslint-disable-next-line react-hooks/rules-of-hooks
useStaticRendering(typeof window === 'undefined');

const I18nAccessBootstrapper = () => {
    const i18n = useI18n();
    const udData = useUDData();
    const udLink = useUDLink();

    useEffect(() => {
        if (!udData.isGlobalMeContextLoading) {
            ShoppingClient.initialize({
                i18n,
                udLink,
                isShoppingCartFeatureEnabled: udData.Config.features.shopping_cart,
            });

            if (udData.me.is_authenticated && udData.me.id) {
                ShoppingClient.setUserId(udData.me.id);
            }

            ShoppingClient.fetch();
        }
    }, [i18n, udData, udLink]);

    return null;
};

/**
 * Injects the FBT bundle redesign config into context. The cart success modal
 * builds the BundleUnit internally, so config flows via context rather than props.
 */
const FbtBundleConfigProvider = ({children}: {children: ReactNode}) => (
    <FbtBundleConfigContext.Provider
        value={{
            excludeAddedCourseFromBundle: true, // drop the just-added course (2 courses, not 3)
            showPerCardAddToCart: true, // per-card ATC button + horizontal product-card layout
            hideRelatedLabelsList: true, // remove the related-topics pills
            wideSuccessModal: true, // wider modal (maps to CartSuccessModal size='wide')
        }}
    >
        {children}
    </FbtBundleConfigContext.Provider>
);

export default function App({
    Component,
    locale, // NOTE: this is just the language portion of the locale, i.e. en instead of en_US
    pageProps,
}: AppWithLayoutProps) {
    // Get application context and render mode
    const {appContext, renderMode} = pageProps;

    const router = useRouter();
    const marketingURL = `${META_DEFAULT_BASE_URL}${router?.asPath ?? ''}`;
    const subscriptionPlanQueryVariables = useMemo(
        () =>
            SUBSCRIPTION_PLAN_PRODUCT_TYPES.map((productType) => ({
                filters: {productType},
                marketingURL,
            })),
        [marketingURL],
    );

    // resolve full locale
    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';

    // Initialize root store
    const [rootStore] = useState(
        () =>
            new RootStore({
                footerData: appContext.footer,
                headerData: appContext.header,
                locale: fullLocale,
            }),
    );
    const [integrationsStore] = useState(() => new IntegrationsStore());

    // Shim device types
    const {isMobile, isDesktop, isTablet} = useDevice();

    // local UD data state
    const [udData, setUDData] = useState(() => {
        const initUDData = mergeWithArrayOverwrite(appContext.udData, {
            Config: {
                app_name: process.env.APP_NAME,
                features: {
                    notice: {
                        smart_bar: true,
                    },
                },
            } as unknown as UDDataConfig,
            userAgnosticTrackingParams: {
                page_key: pageProps.pageKey,
            },
            isGlobalMeContextLoading: true,
            request: {
                isMobile: isMobile,
                isPC: isDesktop,
                isTablet: isTablet,
                language: locale,
                locale: fullLocale,
            },
        });
        return initUDData;
    });

    // Shim UD.performance global API
    shimUDPerformance();

    useEffect(() => {
        async function bootstrapClient() {
            // Get client UD data.  If we are in SSR mode, the data is already available in scope
            const {udData: clientUDData} =
                renderMode === RENDER_MODES.SSR ? await {udData} : await getClientAppContext();

            const fullUdData: UDData = mergeWithArrayOverwrite(udData, clientUDData);

            // Ensure we've set user context has loaded flag
            const fullUdDataWithOverrides: UDData = mergeWithArrayOverwrite(fullUdData, {
                isGlobalMeContextLoading: false,
                /* needed to enable smart-bar */
                Config: {
                    features: {
                        notice: {
                            smart_bar: true,
                        },
                    },
                },
            });

            // Update udData
            setUDData(fullUdDataWithOverrides);

            // If you are SSRing the page, you need to write global UD data to the window object.
            // This will be eventually replaced by a standard app context provider.
            if (renderMode === RENDER_MODES.SSR) {
                const appContextData = {
                    footer: appContext.footer,
                    header: appContext.header,
                    udData: fullUdDataWithOverrides,
                };
                Object.assign(window, {
                    [APP_CONTEXT_VAR_NAME]: appContextData,
                });
            }

            // Initialize application stores
            rootStore.initializeClient();
            integrationsStore.initializeClient();

            // Turn on keyboard navigation detection
            detectKeyboardNavigation();

            return fullUdDataWithOverrides;
        }

        bootstrapClient();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    // Use the layout defined at the page level, if available
    // https://nextjs.org/docs/basic-features/layouts#per-page-layouts
    const getLayout = Component.getLayout ?? ((page) => page);
    const content = getLayout(<Component {...pageProps} />);

    return (
        <>
            <I18nProvider lang={locale} locale={fullLocale} translations={pageProps.translations}>
                <UDDataProvider data={udData as UDData} writeToGlobal={true}>
                    <CommonStoresProvider>
                        <StoreProvider stores={[rootStore, integrationsStore]}>
                            <QueryClientProvider client={queryClient}>
                                <Hydrate state={pageProps.dehydratedState}>
                                    <SubscriptionPlansProvider
                                        subscriptionPlanQueryVariables={
                                            subscriptionPlanQueryVariables
                                        }
                                    >
                                        <ExperimentationProvider
                                            features={EXPERIMENTATION_FEATURES}
                                        >
                                            <BindI18nGlobals />
                                            <I18nAccessBootstrapper />
                                            <FbtBundleConfigProvider>
                                                {content}
                                            </FbtBundleConfigProvider>
                                        </ExperimentationProvider>
                                    </SubscriptionPlansProvider>
                                </Hydrate>
                            </QueryClientProvider>
                        </StoreProvider>
                    </CommonStoresProvider>
                </UDDataProvider>
            </I18nProvider>
        </>
    );
}

// Do not resolve anything user specific in this function.
App.getInitialProps = async function (appContext: AppContext) {
    /**
     * Required step from https://nextjs.org/docs/advanced-features/custom-app#caveats
     *
     * When you add getInitialProps in your custom app, you must import App from "next/app",
     * call App.getInitialProps(appContext) inside getInitialProps and merge the returned object into the return value.
     */
    const locale = appContext.router.locale as string;
    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';
    const [nextAppContext] = await Promise.all([NextApp.getInitialProps(appContext)]);
    return {
        ...nextAppContext,
        locale,
        pageProps: {
            ...nextAppContext.pageProps,
            direction: resolveLocaleDirection(fullLocale as UdemyLocale),
        },
    };
};
